import { useAdmin } from "./AdminContext";
import { Upload, Trash2, Film, Images, Sliders } from "lucide-react";
import { Label } from "@/components/ui/label";

export function AdminGallery() {
  const { completedWorks, worksLoading, completedWorkRef, uploadingWork, handleUploadWork, handleDeleteWork, galleryVideos, videosLoading, videoRef, uploadingVideo, handleUploadVideo, handleDeleteVideo, settings, setSettings, saveGallerySpeed } = useAdmin();

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-heading text-2xl font-black uppercase">Completed Works Gallery</h2>
          <p className="text-sm text-zinc-500 mt-1">{completedWorks.length} images, {galleryVideos.length} videos displayed on the landing page</p>
        </div>
        <div className="flex items-center gap-3">
          <input ref={completedWorkRef} type="file" accept="image/*" className="hidden" onChange={handleUploadWork} />
          <button onClick={() => completedWorkRef.current?.click()} disabled={uploadingWork} className={`brutal-btn text-sm flex items-center gap-2 ${uploadingWork ? "opacity-50" : ""}`} data-testid="upload-work-btn"><Upload size={16} strokeWidth={2.5} /> {uploadingWork ? "Uploading..." : "Add Image"}</button>
          <input ref={videoRef} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={handleUploadVideo} />
          <button onClick={() => videoRef.current?.click()} disabled={uploadingVideo} className={`brutal-btn brutal-btn-secondary text-sm flex items-center gap-2 ${uploadingVideo ? "opacity-50" : ""}`} data-testid="upload-video-btn"><Film size={16} strokeWidth={2.5} /> {uploadingVideo ? "Uploading..." : "Add Video"}</button>
        </div>
      </div>

      {/* Gallery Speed Control */}
      <div className="brutal-card p-5 mb-8 flex items-center gap-6" data-testid="gallery-speed-control">
        <div className="flex items-center gap-2"><Sliders size={16} strokeWidth={2.5} /><Label className="font-heading font-bold text-sm uppercase">Scroll Speed</Label></div>
        <div className="flex-1 flex items-center gap-4">
          <span className="text-xs font-mono text-zinc-500">Slow</span>
          <input type="range" min="5" max="100" value={settings.gallery_speed || 30} onChange={e => setSettings(p => ({ ...p, gallery_speed: parseInt(e.target.value) }))} onMouseUp={e => saveGallerySpeed(parseInt(e.target.value))} onTouchEnd={e => saveGallerySpeed(parseInt(e.target.value))} className="flex-1 accent-[#FF3366] h-2" data-testid="gallery-speed-slider" />
          <span className="text-xs font-mono text-zinc-500">Fast</span>
          <span className="font-mono font-bold text-sm w-8 text-center">{settings.gallery_speed || 30}</span>
        </div>
      </div>

      {/* Works Grid */}
      {worksLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">{[1, 2, 3, 4].map(i => <div key={i} className="brutal-card h-40 animate-pulse" />)}</div>
      ) : completedWorks.length === 0 ? (
        <div className="brutal-card p-12 text-center" data-testid="no-works">
          <Images size={48} className="mx-auto mb-4 text-zinc-300" strokeWidth={2.5} /><h3 className="font-heading text-lg font-bold mb-2">No Gallery Images</h3>
          <button onClick={() => completedWorkRef.current?.click()} className="brutal-btn text-sm"><Upload size={14} className="inline mr-2" strokeWidth={2.5} /> Upload First Image</button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="works-grid">
          {completedWorks.map(work => (
            <div key={work.id} className="brutal-card group relative" data-testid={`work-${work.id}`}>
              <div className="h-40 bg-zinc-100 border-b-2 border-black overflow-hidden"><img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${work.storage_path}`} alt={work.name} className="w-full h-full object-contain p-1" /></div>
              <div className="p-3 flex items-center justify-between">
                <span className="text-xs text-zinc-500 truncate">{work.name}</span>
                <button onClick={() => handleDeleteWork(work.id)} className="text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-work-${work.id}`}><Trash2 size={14} strokeWidth={2.5} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Video Gallery */}
      <div className="mt-12">
        <h3 className="font-heading text-xl font-black uppercase mb-6 flex items-center gap-2"><Film size={20} strokeWidth={2.5} /> Video Gallery</h3>
        {videosLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">{[1, 2, 3].map(i => <div key={i} className="brutal-card h-40 animate-pulse" />)}</div>
        ) : galleryVideos.length === 0 ? (
          <div className="brutal-card p-12 text-center" data-testid="no-videos">
            <Film size={48} className="mx-auto mb-4 text-zinc-300" strokeWidth={2.5} /><h3 className="font-heading text-lg font-bold mb-2">No Gallery Videos</h3>
            <button onClick={() => videoRef.current?.click()} className="brutal-btn brutal-btn-secondary text-sm"><Film size={14} className="inline mr-2" strokeWidth={2.5} /> Upload First Video</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="videos-grid">
            {galleryVideos.map(video => (
              <div key={video.id} className="brutal-card group relative" data-testid={`video-${video.id}`}>
                <div className="h-48 bg-black border-b-2 border-black overflow-hidden"><video src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${video.storage_path}`} className="w-full h-full object-contain" controls muted /></div>
                <div className="p-3 flex items-center justify-between">
                  <span className="text-xs text-zinc-500 truncate">{video.name}</span>
                  <button onClick={() => handleDeleteVideo(video.id)} className="text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-video-${video.id}`}><Trash2 size={14} strokeWidth={2.5} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
